import { useContext, useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { AppContainer, EditContainer, OnboardingContainer, SignOutContainer, SmartMeterContainer } from '../containers';
import { AppContext } from '../context/AppContext';
import ContextProvider from '../context/ContextProvider';
import { useActiveSubscription, useSystemFeatures } from '../hooks';
import useAuthDomainPrefixGuard from '../hooks/useAuthDomainPrefixGuard';
import { getCurrentUserInfo } from '../utils/auth';

import { AppGate } from './AppGate';

interface UserAttributes {
  [key: string]: string | undefined;
}

interface OptimiseUser {
  attributes?: UserAttributes;
}

const HomeRedirect: React.FC<{ isOnboarded: boolean }> = ({ isOnboarded }) =>
  isOnboarded ? <Navigate to="/app" /> : <Navigate to="/onboarding" />;

const AppRouterWithContext: React.FC = () => {
  useSystemFeatures();
  useAuthDomainPrefixGuard({
    requireClaim: true,
    redirectOnMismatch: true,
  });

  const {
    isOnboarded,
    setIsOnboarded,
    subscriptionsDisabled,
    currentUserInfo,
    setCurrentUserInfo,
    isAdmin,
    setIsAdmin,
  } = useContext(AppContext) as {
    isOnboarded: boolean;
    setIsOnboarded: (v: boolean) => void;
    subscriptionsDisabled: boolean;
    currentUserInfo: OptimiseUser | undefined;
    setCurrentUserInfo: (currentUserInfo: OptimiseUser | undefined) => void;
    isAdmin: boolean;
    setIsAdmin: (v: boolean) => void;
  };
  const { activeSubscription, fetchedSubscriptions } = useActiveSubscription();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchUserInfo = async () => {
      let userInfo: OptimiseUser | undefined;
      try {
        if (!!currentUserInfo?.attributes && currentUserInfo.attributes['custom:is_onboarded'] === 'true') {
          userInfo = currentUserInfo;
        } else {
          setLoading(true);
          userInfo = (await getCurrentUserInfo()) as OptimiseUser | undefined;
          setCurrentUserInfo(userInfo);
        }
        if (cancelled) return;
        const onboarded = !!userInfo?.attributes && userInfo.attributes['custom:is_onboarded'] === 'true';
        setIsOnboarded(onboarded);
        setIsAdmin(!!(userInfo?.attributes && 'custom:admin_secret' in userInfo.attributes));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchUserInfo();
    return () => {
      cancelled = true;
    };
  }, [setIsOnboarded, setIsAdmin]);

  // Unified gating logic
  const isLoading = loading || (!subscriptionsDisabled && !fetchedSubscriptions);
  const hasActiveSubscription = subscriptionsDisabled || !!activeSubscription;
  const canAccessApp = isOnboarded && hasActiveSubscription;
  const canAccessOnboarding = !isOnboarded || !hasActiveSubscription;
  const needsSubscription = !isAdmin && isOnboarded && !subscriptionsDisabled && !activeSubscription;

  const guardContext = {
    loading: isLoading,
    canAccessApp,
    canAccessOnboarding,
    needsSubscription,
  };

  return (
    <Router>
      <AppGate ctx={guardContext}>
        <Routes>
          <Route path="/onboarding/*" element={<OnboardingContainer />} />
          <Route path="/app/*" element={<AppContainer />} />
          <Route path="/smart-meter/*" element={<SmartMeterContainer />} />
          <Route path="/edit/*" element={<EditContainer />} />
          <Route path="/sign-out" element={<SignOutContainer />} />
          <Route path="/" element={<HomeRedirect isOnboarded={isOnboarded} />} />
        </Routes>
      </AppGate>
    </Router>
  );
};

const AppRouter = () => (
  <ContextProvider>
    <AppRouterWithContext />
  </ContextProvider>
);

export default AppRouter;
